<template>
  <component
    :is="type"
    :href="href"
    :type="submit"
    :disabled="!active"
    :class="[
      widget ? 'binds-ds-button' : 'binds-button',
      size,
      state,
      variation,
      outline && 'outline',
      disabled && 'disabled',
      inline && 'inline',
      inside && 'inside',
      floating && 'floating',
      dark && 'dark',
    ]"
    v-bind="$attrs"
    v-on="{
      ...$listeners,
      input: event => $emit('input', event.target.value)
    }"
  >
    <div class="button-icon text-center" v-if="!!icon">
      <icon :name="icon" :size="iconSize" :color="iconColor"/>
    </div>
    <div>
      <slot/>
    </div>
  </component>
</template>

<script>
/**
 * Buttons are generally used for interface actions. Suitable for all-purpose use.
 * Defaults to appearance that has white background with grey border.
 * Primary style should be used only once per view for main call-to-action.
 */
import Icon from '../Icon/Icon.vue'

export default {
  name: 'AppButton',
  status: 'prototype',
  release: '3.5.0',
  components: { Icon },
  props: {
    /**
     * The html element used for the button.
     * `button, a`
     */
    type: {
      type: String,
      default: 'button',
      validator: value => {
        return value.match(/(button|a)/)
      }
    },
    /**
     * The size of the button. Defaults to medium.
     * `small, medium, large`
     */
    size: {
      type: String,
      default: 'medium',
      validator: value => {
        return value.match(/(small|medium|large)/)
      }
    },
    /**
     * When setting the button’s type to a link, use this option to give a href.
     */
    href: {
      type: String,
      default: null
    },
    active: {
      type: Boolean,
      default: true
    },
    /**
     * Set the button’s type to “submit”.
     */
    submit: {
      type: String,
      default: null,
      validator: value => {
        return value.match(/(null|submit)/)
      }
    },
    /**
     * Manually trigger various states of the button.
     * `hover, active, focus`
     */
    state: {
      type: String,
      default: null,
      validator: value => {
        return value.match(/(hover|active|focus)/)
      }
    },
    /**
     * Style variation to give additional meaning.
     * `primary, secondary`
     */
    variation: {
      type: String,
      default: 'default',
      validator: value => {
        return value.match(/(default|primary|secondary)/)
      }
    },
    /**
     * Style inline to give additional meaning.
     * `inline`
     */
    inline: {
      type: Boolean,
      default: false
    },
    /**
     * Style inside to give additional meaning.
     * `inside`
     */
    inside: {
      type: Boolean,
      default: false
    },
    /**
     * Style floating to give additional meaning.
     * `floating`
     */
    floating: {
      type: Boolean,
      default: false
    },
    /**
     * Style outline to give additional meaning.
     * `outline`
     */
    outline: {
      type: Boolean,
      default: false
    },
    /**
     * Style disabled to give additional meaning.
     * `disabled`
     */
    disabled: {
      type: Boolean,
      default: false
    },
    dark: {
      type: Boolean,
      default: false
    },
    widget: {
      type: Boolean,
      default: false
    },
    icon: {
      type: String,
      default: null
    },
    iconColor: {
      type: String,
      default: null
    },
    iconSize: {
      type: String,
      default: 'md',
      validator: value => {
        return value.match(/(xxs|xs|sm|md|lg|xl|xxl)/)
      }
    }
  }
}
</script>

<style lang="scss">
@import "../../styles/styles.scss";
.binds-ds-button,
.binds-button {
  @include reset;
  @include stack-space($space-m);
  @include inline-space($space-xs);
  will-change: transform;
  transition: all 0.2s ease;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  font-weight: $weight-semi-bold;
  font-size: $size-m;
  font-family: $font-text;
  line-height: $line-height-m;
  text-decoration: none;
  display: inline-block;
  align-items: center;
  justify-content: center;
  border: 0;
  box-shadow: inset 0 0 0 2px $color-pink-lighter;
  border-radius: $radius-default;
  background: transparent;
  color: $color-pink-lighter;
  cursor: pointer;
  &:hover,
  &.hover {
    color: $color-white;
    background: $color-pink-lighter;
    transform: translateZ(0) scale(1.03);
  }
  &:active,
  &.active {
    transition: none;
    background: $color-pink-light;
    box-shadow: none;
    color: $color-white;
    transform: translateZ(0) scale(1);
  }

  &:focus,
  &.focus {
    background: $color-pink;
    box-shadow: none;
    color: $color-white;
    transform: translateZ(0) scale(1);
    outline: 0;
  }

  // For icons inside buttons
  .icon {
    float: right;
    margin: -#{$space-xs} -#{$space-xs} -#{$space-s} $space-xs/2;
    color: $color-pink-lighter;
  }

  // Various button sizes
  &.large {
    @include inset-squish-space($space-s);
    font-size: $size-l;
    &.floating {
      width: 64px;
      height: 64px;
    }
  }
  &.medium {
    @include inset-squish-space($space-s);
    font-size: $size-m;
    &.floating {
      width: 45px;
      height: 45px;
    }
  }
  &.small {
    @include inset-squish-space($space-xs);
    font-size: $size-s;
    &.floating {
      width: 35px;
      height: 35px;
    }
  }

  &.floating {
    display: inline-flex;
    .button-icon {
      display: inline-flex;
      justify-self: center;
      align-self: center;
    }
  }
  // Default button
  &.default {
    background: $color-gray-light;
    color: $color-white;
    box-shadow: 0 5px 15px 0 rgba(181, 181, 181, 0.5);
    &.dark {
      box-shadow: none;
    }
    &:hover,
    &.hover {
      background-color: shade($color-gray-light, 12%);
    }
    &:active,
    &.active {
      background-color: shade($color-gray-light, 20%);
      transition: none;
    }
    &:focus {
      outline: 0;
    }
    .user-is-tabbing &:focus,
    &.focus {
    }
    &.outline {
      border: 0;
      box-shadow: inset 0 0 0 2px $color-gray-light;
      border-radius: $radius-default;
      background: transparent;
      color: $color-gray-light;
    }
    &.floating {
      font-size: $size-m;
      border: 0;
      box-shadow: inset 0 0 0 2px $color-gray-light;
      border-radius: $radius-circle;
    }
    &.inline {
      border: 0;
      background: transparent;
      color: $color-gray-light;
    }
    &.inside {
      border: 0;
      background: transparent;
      color: $color-gray-light;
      text-decoration: underline;
      display: inline-block;
    }
  }

  // Primary button
  &.primary {
    background: $color-pink-lighter;
    color: $color-white;
    box-shadow: 0 5px 10px 0 rgba(255, 96, 125, 0.26);
    &.dark {
      box-shadow: none;
    }
    &:hover,
    &.hover {
      background-color: shade($color-pink-lighter, 12%);
    }
    &:active,
    &.active {
      background-color: shade($color-pink-lighter, 20%);
      transition: none;
    }
    &:focus {
      outline: 0;
    }
    .user-is-tabbing &:focus,
    &.focus {
    }
    &.outline {
      border: 0;
      box-shadow: inset 0 0 0 2px $color-pink-lighter;
      border-radius: $radius-default;
      background: transparent;
      color: $color-pink-lighter;
    }
    &.floating {
      box-shadow: inset 0 0 0 2px $color-pink-lighter;
      font-size: $size-m;
      border: 0;
      border-radius: $radius-circle;
    }
    &.inline {
      color: $color-pink-lighter;
    }
    &.inside {
      color: $color-pink-lighter;
      text-decoration: underline;
      display: inline-block;
    }
  }

  // secondary button
  &.secondary {
    background: $color-purple-dark;
    color: $color-white;
    box-shadow: 0 5px 15px 0 rgba(152, 113, 221, 0.5);
    &.dark {
      box-shadow: none;
    }
    &:hover,
    &.hover {
      background-color: shade($color-purple-dark, 12%);
    }
    &:active,
    &.active {
      background-color: shade($color-purple-dark, 20%);
      transition: none;
    }
    &:focus {
      outline: 0;
    }
    .user-is-tabbing &:focus,
    &.focus {
    }
    &.outline {
      border: 0;
      box-shadow: inset 0 0 0 2px $color-purple-dark;
      border-radius: $radius-default;
      background: transparent;
      color: $color-purple-dark;
    }
    &.floating {
      border: 0;
      box-shadow: inset 0 0 0 2px $color-purple-dark;
      font-size: $size-m;
      border-radius: $radius-circle;
    }
    &.inline {
      color: $color-purple-dark;
    }
    &.inside {
      border: 0;
      color: $color-purple-dark;
      text-decoration: underline;
      display: inline-block;
    }
  }

  &.inline,
  &.inside {
    border: 0;
    background: transparent;
    box-shadow: none;
  }
}
</style>
